import { ReactElement, ReactNode, useEffect, useRef, useState } from 'react';
import { components, InputProps as RSelectInputProps, OptionTypeBase } from 'react-select';
import AsyncSelect, { AsyncProps } from 'react-select/async';

import { calculateTextWidth } from '@components/component-helpers';
import { FormLabel } from '@components/form-label';
import { IconProps } from '@components/icons';
import { IconDropdown } from '@components/icons/dropdown';
import { IconLock } from '@components/icons/lock';
import { IconReturn } from '@components/icons/return';
import { Stack } from '@components/stack';

import { useSelectTranslations } from './locale';
import {
  InputBox,
  styles,
  FocusIconContainer,
  OptionContainer,
  HelpText,
  PrefixContainer,
  Prefix,
} from './styles';

interface SelectAsyncOption {
  label: string | ReactNode;
  value: string;
  helpText?: string;
}

export interface SelectAsyncProps<T extends OptionTypeBase = OptionTypeBase> extends AsyncProps<T> {
  options?: SelectAsyncOption[];
  defaultValue?: string;
  onChange: (value: any) => void;
  placeholder?: string;
  noOptions?: (obj: { inputValue: string }) => string | null;
  label?: string;
  labelIcon?: ReactElement<IconProps>;
  loadingMessage?: (obj: { inputValue: string }) => string | null;
  isDisabled?: boolean;
  prefix?: string;
}

const DropdownIndicator = (props: any) => {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        {props.isDisabled ? <IconLock /> : <IconDropdown />}
      </components.DropdownIndicator>
    )
  );
};

const Option = (props: any) => {
  return (
    <components.Option {...props}>
      <FocusIconContainer>
        <OptionContainer $helpText={props.data.helpText}>
          {props.children}
          {props.data.helpText && <HelpText>{props.data.helpText}</HelpText>}
        </OptionContainer>
        {props.isFocused && <IconReturn color="purple50" />}
      </FocusIconContainer>
    </components.Option>
  );
};

const SingleValue = (props: any) => {
  return (
    <components.SingleValue className="single-value-container" {...props}>
      {props.selectProps.prefix && (
        <PrefixContainer>
          <Prefix>{props.selectProps.prefix}</Prefix>
          {props.children}
        </PrefixContainer>
      )}
      {!props.selectProps.prefix && props.children}
    </components.SingleValue>
  );
};

export function SelectAsync<T extends OptionTypeBase = OptionTypeBase>(props: SelectAsyncProps<T>) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isInsideModal, setIsInsideModal] = useState(false);
  const { t } = useSelectTranslations();

  const {
    options,
    defaultValue,
    onChange,
    placeholder = t('placeholder'),
    noOptions = () => t('no_options'),
    label,
    labelIcon,
    defaultOptions,
    loadOptions,
    loadingMessage,
    isDisabled = false,
    prefix,
  } = props;

  useEffect(() => {
    const modalEl = document.getElementById('modal');
    if (!modalEl) return;

    if (modalEl.contains(ref.current)) {
      setIsInsideModal(true);
    }
  }, []);

  return (
    <Stack space={1} ref={ref}>
      <FormLabel text={label} icon={labelIcon} />
      <AsyncSelect
        isDisabled={isDisabled}
        defaultValue={defaultValue || undefined}
        onChange={(obj) => onChange(obj.value)}
        menuPlacement="auto"
        defaultOptions={defaultOptions}
        loadOptions={loadOptions}
        openMenuOnFocus
        menuShouldBlockScroll={isInsideModal}
        menuPortalTarget={isInsideModal ? document.body : null}
        noOptionsMessage={noOptions}
        closeMenuOnSelect={true}
        loadingMessage={loadingMessage}
        styles={styles}
        isClearable={false}
        isSearchable={true}
        placeholder={placeholder}
        options={options}
        prefix={prefix}
        components={{
          Option,
          SingleValue,
          Input: CustomInput,
          IndicatorSeparator: () => null,
          DropdownIndicator,
        }}
      />
    </Stack>
  );
}

function CustomInput(props: RSelectInputProps) {
  let {
    innerRef,
    cx,
    getStyles,
    theme,
    isHidden,
    isDisabled,
    // @ts-ignore
    value,
    ...rest
  } = props;
  return (
    <InputBox
      // @ts-ignore stfu
      ref={innerRef}
      style={{
        width: calculateTextWidth(value, "normal 400 16px 'Work Sans'"),
      }}
      value={value}
      {...rest}
    />
  );
}
